<script setup>
import { buildProfileUrl, getFullUrl, displayPhone } from '@/plugins/misc'
import { mediaUrl } from '@core/utils/mediaUrl'
import MemberVerifiedTags from '@core/components/MemberVerifiedTags.vue'
import logoLargeLight from '@/assets/logo.svg'

defineProps({
  member: {
    type: Object,
    required: true,
  },
  // When true, show an edit (pencil) affordance on the avatar — used by the
  // request flow's Host Profile step. Other usages leave it off.
  editable: {
    type: Boolean,
    default: false,
  },
  // When true, drop the grey card surface and render on a plain (white)
  // background, separated by a light bottom divider instead of a box. Used on the
  // reservation review screen so the profile reads as a section, not a card.
  flat: {
    type: Boolean,
    default: false,
  },
  // Their Favorites and Connections counts, as on their profile page — for a
  // restaurant's view of a member (the Fans page, the pop-up from Messages;
  // Kapil, 2026-09-25). Other usages leave it off.
  showStats: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['edit'])
</script>

<template>
  <div
    v-if="member"
    class="member-profile-card"
    :class="{ 'member-profile-card--flat': flat }"
  >
    <div class="member-avatar-row">
      <div class="member-avatar">
        <VImg
          v-if="member?.profileImageId"
          class="member-avatar-image"
          :src="mediaUrl(member?.profileImageId, 'small')"
          cover
        />
        <div
          v-else
          class="member-avatar-fallback"
        >
          <span class="member-avatar-initials">
            {{ member?.firstName?.[0]?.toUpperCase() }}{{ member?.lastName?.[0]?.toUpperCase() }}
          </span>
        </div>
      </div>
    </div>

    <div class="member-profile-name-row">
      <div class="member-profile-name">
        <span class="member-profile-name-text">{{ member.fullName }}</span><span
          v-if="member?.verified || member.membershipType"
          class="member-profile-name-badges"
        >
          <VIcon
            v-if="member?.verified"
            size="18"
            class="member-verified-icon"
          >
            material-symbols:verified
          </VIcon>
          <span
            v-if="member.membershipType"
            class="member-profile-membership"
          >
            ({{ member.membershipType }})
          </span>
        </span>
      </div>
    </div>

    <!-- Optional affinity slot rendered directly under the name (used by the reservation review step). -->
    <slot name="affinity" />

    <div class="member-profile-titles">
      <div
        v-if="member?.title && member?.company"
        class="member-profile-role"
      >
        {{ member.title }}, {{ member.company }}
      </div>
      <div
        v-else-if="member?.company"
        class="member-profile-role"
      >
        {{ member.company }}
      </div>
      <div
        v-if="member?.title2 && member?.company2"
        class="member-profile-role-secondary"
      >
        {{ member.title2 }}, {{ member.company2 }}
      </div>
      <div
        v-else-if="member?.company2"
        class="member-profile-role-secondary"
      >
        {{ member.company2 }}
      </div>
    </div>

    <div
      v-if="editable"
      class="member-profile-edit"
    >
      <VBtn
        variant="text"
        density="compact"
        size="small"
        color="primary-dark"
        @click="emit('edit')"
      >
        Edit profile
      </VBtn>
    </div>

    <div
      v-if="member.membershipType == 'Trial' && member.referredBy"
      class="member-profile-referred"
    >
      <VIcon
        size="14"
        class="mr-1"
      >
        mdi-account-arrow-right
      </VIcon>
      Referred by <b>{{ member.referredBy }}</b>
    </div>

    <div
      v-if="member?.bio"
      class="member-profile-bio"
    >
      <span class="member-profile-bio-label">Bio:</span> {{ member.bio }}
    </div>

    <div
      v-if="member?.verifiedTags?.length > 0"
      class="member-profile-tags"
    >
      <MemberVerifiedTags :member="member" />
    </div>

    <div
      v-if="showStats && (member?.favoritedRestaurantsCount || member?.membersWhoAreFollowingCount)"
      class="member-profile-stats"
    >
      <span v-if="member?.favoritedRestaurantsCount">
        <b>{{ member.favoritedRestaurantsCount }}</b> Favorites
      </span>
      <span v-if="member?.membersWhoAreFollowingCount">
        <b>{{ member.membersWhoAreFollowingCount }}</b> Connections
      </span>
    </div>

    <div class="member-profile-social">
      <a
        v-if="member?.profileLinkedInProfileId"
        :href="buildProfileUrl('linkedin', member?.profileLinkedInProfileId)"
        target="_blank"
        rel="noopener noreferrer"
        class="member-social-chip"
      >
        <VIcon size="16">mdi-linkedin</VIcon>
        <span>LinkedIn</span>
      </a>
      <a
        v-if="member?.profileInstagramProfileId"
        :href="buildProfileUrl('instagram', member?.profileInstagramProfileId)"
        target="_blank"
        rel="noopener noreferrer"
        class="member-social-chip"
      >
        <VIcon size="16">mdi-instagram</VIcon>
        <span>Instagram</span>
      </a>
      <a
        v-if="member?.profileXProfileId"
        :href="buildProfileUrl('twitter', member?.profileXProfileId)"
        target="_blank"
        rel="noopener noreferrer"
        class="member-social-chip"
      >
        <VIcon size="16">simple-icons:x</VIcon>
        <span>X</span>
      </a>
      <a
        v-if="member?.profileTikTokProfileId"
        :href="buildProfileUrl('tiktok', member?.profileTikTokProfileId)"
        target="_blank"
        rel="noopener noreferrer"
        class="member-social-chip"
      >
        <VIcon size="16">simple-icons:tiktok</VIcon>
        <span>TikTok</span>
      </a>
      <a
        v-if="member?.profileWebsiteLink"
        :href="getFullUrl(member?.profileWebsiteLink)"
        target="_blank"
        rel="noopener noreferrer"
        class="member-social-chip"
      >
        <VIcon size="16">mdi-web</VIcon>
        <span>Website</span>
      </a>
      <a
        v-if="member?.profileId"
        :href="`/m/${member?.profileId}`"
        target="_blank"
        rel="noopener noreferrer"
        class="member-social-chip member-social-chip--rm"
      >
        <img
          :src="logoLargeLight"
          width="16"
        >
        <span>RM Profile</span>
      </a>
    </div>

    <div
      v-if="member?.phoneNumber || member?.email"
      class="member-profile-contact"
    >
      <div
        v-if="member?.phoneNumber"
        class="member-contact-item"
      >
        <VIcon
          size="14"
          color="primary"
        >
          mdi-phone-outline
        </VIcon>
        <span>{{ displayPhone(member.phoneNumber) }}</span>
      </div>
      <div
        v-if="member?.email"
        class="member-contact-item"
      >
        <VIcon
          size="14"
          color="primary"
        >
          mdi-email-outline
        </VIcon>
        <span>{{ member.email }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$text-dark: #1a1a1a;
$text-medium: #555;
$text-light: #777;

.member-profile-card {
  text-align: center;
  background: #f8f8f9;
  border-radius: 12px;
  padding: 20px 16px;
}

/* White variant: no grey surface, separated from the content below by a light
   divider instead of a box (reservation review screen). */
.member-profile-card--flat {
  background: transparent;
  border-radius: 0;
  padding: 4px 0 16px;
  border-bottom: 1px solid var(--ds-border-light, #e5e5e5);
}

.member-avatar-row {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.member-profile-edit {
  text-align: center;
  margin-bottom: 8px;

  // 14px to match the request flow's floor (only renders when editable, so the
  // member Profile page's own Edit-profile button is unaffected).
  :deep(.v-btn) {
    font-size: 14px;
  }
}

.member-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--ds-brand-primary-light);
  outline: 3px solid rgba(69, 182, 169, 0.25);
  outline-offset: 3px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.member-avatar-image {
  border-radius: 50%;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.member-avatar-initials {
  font-size: 24px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark);
}

.member-profile-name-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.member-profile-name {
  font-size: 20px;
  font-weight: 700;
  color: $text-dark;
  line-height: 1.3;
  word-break: break-word;
}

.member-profile-name-badges {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
}

.member-verified-icon {
  color: rgb(0, 149, 246);
}

.member-profile-membership {
  font-size: 14px;
  font-weight: 500;
  color: $text-dark;
}

.member-profile-titles {
  margin-bottom: 8px;
}

.member-profile-role {
  font-size: 14px;
  font-weight: 500;
  color: $text-medium;
  line-height: 1.4;
}

.member-profile-role-secondary {
  font-size: 14px;
  color: $text-light;
  margin-top: 2px;
  line-height: 1.3;
}

.member-profile-referred {
  font-size: 14px;
  color: $text-medium;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.member-profile-tags {
  margin-bottom: 8px;

  :deep(.flex) {
    justify-content: center;
  }
}

.member-profile-bio {
  font-size: 14px;
  color: $text-medium;
  line-height: 1.5;
  margin-bottom: 12px;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.member-profile-bio-label {
  font-weight: 700;
  color: $text-dark;
}

.member-profile-social {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.member-social-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid var(--ds-border-light);
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: $text-medium;
  text-decoration: none;
  transition: all 0.15s;

  &:hover {
    background: var(--ds-brand-primary-light);
    border-color: var(--ds-brand-primary);
    color: var(--ds-brand-primary-dark);
  }

  &--rm {
    border-color: var(--ds-brand-primary);
    color: var(--ds-brand-primary-dark);
  }
}

.member-profile-contact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid #e8e8e8;
}

.member-contact-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: $text-medium;
}

/* Favorites and Connections, as the profile page shows them. */
.member-profile-stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 16px;
  margin: 12px 0 4px;
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.member-profile-stats b {
  font-weight: 600;
  color: var(--ds-text-strong, rgb(var(--v-theme-on-surface)));
}
</style>
